import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import { BRANDING } from '../../config/branding';
import useCheckoutConversion from '../../utils/hooks/useCheckoutConversion';
import { login, sendPasswordResetEmail, getPostLoginDestination } from '../../services/Firebase/authHelper';
import {
  AuthActionButton,
  AuthMessage,
  Card,
  CardHeader,
  CardSubtitle,
  FieldLabel,
  FieldWrap,
  Form,
  Icon,
  Input,
  LinkButton,
  Page,
  Title,
} from './styles';

const Login = () => {
  const history = useHistory();
  const location = useLocation();
  useCheckoutConversion(location.search);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('login');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setMessage('');
  }, [mode]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const checkout = query.get('checkout');
    const checkoutEmail = query.get('email');

    if (checkoutEmail) {
      setEmail(checkoutEmail);
    }

    if (checkout === 'success') {
      setMessage('Checkout complete. Your account is ready. Log in now to access your data.');
    }
    if (checkout === 'cancel') {
      setMessage('Checkout was canceled. You can restart purchase anytime.');
    }
  }, [location.search]);

  const formatAuthError = error => {
    const code = error?.code || '';
    const map = {
      'auth/user-not-found': 'No account found with this email. Check your email or request access.',
      'auth/wrong-password': 'Incorrect password. Try again or use Forgot Password.',
      'auth/invalid-credential': 'Email or password is incorrect. Please try again.',
      'auth/invalid-email': "That doesn't look like a valid email address.",
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/too-many-requests': 'Too many attempts. Please wait a few minutes before trying again.',
      'auth/network-request-failed': 'Network error. Check your connection and try again.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/operation-not-allowed': 'This sign-in method is not available. Please contact support.',
      'auth/weak-password': 'Password is too weak. Use at least 8 characters.',
      'auth/requires-recent-login': 'Please sign in again to continue.',
      'auth/invalid-continue-uri': 'Something went wrong. Please try again.',
      'auth/missing-password': 'Please enter your password.',
      'auth/missing-email': 'Please enter your email address.',
    };
    if (map[code]) return map[code];
    const raw = error?.message || '';
    const cleaned = raw.replace(/^Firebase:\s*/i, '').replace(/\s*\(auth\/[^)]+\)\.\s*$/, '').trim();
    return cleaned || 'Something went wrong. Please try again.';
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (mode === 'login') {
        await login(email, password);
        const destination = await getPostLoginDestination();
        history.replace(destination);
      } else {
        await sendPasswordResetEmail(email);
        setMessage('Password reset email sent. Check your inbox.');
      }
    } catch (error) {
      setMessage(formatAuthError(error));
    } finally {
      setLoading(false);
    }
  };

  const loweredMessage = message.toLowerCase();
  const isSuccessMessage = loweredMessage.includes('checkout complete') || loweredMessage.includes('sent');

  return (
    <Page>
      <Helmet>
        <title>{BRANDING.appName} | Login</title>
        <meta name="description" content={BRANDING.seo.defaultDescription} />
      </Helmet>

      <Card>
        <CardHeader>
          <Icon src={BRANDING.iconPath} alt={`${BRANDING.appName} icon`} />
          <Title>{mode === 'login' ? 'Log In' : 'Password Reset'}</Title>
        </CardHeader>

        <CardSubtitle>Use your existing account to get access to game cards and packages.</CardSubtitle>

        {message && <AuthMessage $success={isSuccessMessage}>{message}</AuthMessage>}

        <Form onSubmit={handleSubmit}>
          <FieldWrap>
            <FieldLabel>Email</FieldLabel>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          </FieldWrap>

          {mode === 'login' && (
            <FieldWrap>
              <FieldLabel>Password</FieldLabel>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            </FieldWrap>
          )}

          <AuthActionButton type="submit" disabled={loading}>
            {loading ? 'Working...' : mode === 'login' ? 'Sign In Securely' : 'Recover Password'}
          </AuthActionButton>
        </Form>

        <LinkButton type="button" onClick={() => setMode(mode === 'login' ? 'forgot-password' : 'login')}>
          {mode === 'login' ? 'Forgot Password?' : 'Return to Login'}
        </LinkButton>
      </Card>
    </Page>
  );
};

export default Login;
